import { computed, inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import {
  selectIsTicketAssignmentStepVisible,
  selectIsTicketLimitPerEmailActive,
  uniqueVisitorCheckType
} from '@app/app.reducer';
import {
  DynamicFormField,
  DynamicFormFields,
  FieldKey
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import {
  CheckBuyerInformationFormsValidity,
  CheckBuyerQuestionnaireFormsValidity,
  CheckedFormsValidity,
  CheckedSelectedTicketAssignmentFormsAndFormFieldsValidity,
  CheckedTariffEmailAddressValidity,
  CheckedTariffNameValidity,
  CreatedFormGroups,
  FormError,
  FormValidity,
  InvalidFormField,
  SetCreateFormGroupsFormMediatorForms
} from '@core/data-access/models/form/form-validity.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { FormMediator } from '@core/data-access/services/form/form-mediator.service';
import { FormTicketLimitValidity } from '@core/data-access/services/form/form-ticket-limit-validity';
import { FormValidationProcessing } from '@core/data-access/services/form/form-validation-processing';
import {
  TicketHolderFormState,
  TicketHolderFormStates
} from '@core/features/ticket-assignment/models/ticket-holder-form.model';
import { TicketHolderQuestionnaireFormStates } from '@core/features/ticket-assignment/models/ticket-holder-questionnaire-fom.model';
import {
  selectGroupedNameEmailAddressAndPersonsByTariffUuids,
  selectSelectedTariffUuid
} from '@core/features/ticket-assignment/store/ticket-assignment.selectors';
import { isCheckboxField } from '@core/utils/dynamic-form-field/dynamic-form-field-type-guards';
import { removeHtmlMarkup } from '@core/utils/helpers';
import { Store } from '@ngrx/store';
import { TariffUuid } from '@products/models/tariff.model';
import { UniqueVisitorCheckType } from '@store/customization/customization.interfaces';
import {
  map as _map,
  merge as _merge,
  reduce as _reduce,
  defaultTo,
  every,
  find,
  findKey,
  flatMap,
  forEach,
  fromPairs,
  has,
  head,
  includes,
  invertBy,
  isEmpty,
  isNil,
  isNull,
  keys,
  mapValues,
  pickBy,
  some
} from 'lodash';
import {
  combineLatest,
  debounceTime,
  filter,
  iif,
  map,
  merge,
  Observable,
  of,
  pipe,
  share,
  shareReplay,
  startWith,
  switchMap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FormValidityService {
  readonly #injector = inject(Injector);
  readonly #store = inject(Store);
  readonly #formMediator = inject(FormMediator);
  readonly #formTicketLimitValidity = inject(FormTicketLimitValidity);
  readonly #formValidationProcessing = inject(FormValidationProcessing);

  readonly #groupedNameEmailAddressAndPersonsByTariffUuids$ = this.#store.select(
    selectGroupedNameEmailAddressAndPersonsByTariffUuids
  );

  readonly #checkNameDuplicates$ = this.checkNameDuplicates$();
  readonly #checkEmailAddressDuplicates$ = this.checkEmailAddressDuplicates$();
  readonly #checkMergedTicketAssignmentFormsValidity$ = this.checkMergedTicketAssignmentFormsValidity$();
  readonly #checkOrderOverviewFormsAndFormFieldsValidity$ = this.checkOrderOverviewFormsAndFormFieldsValidity$();
  readonly #checkBuyerInformationFormsAndFormFieldsValidity$ = this.checkBuyerInformationFormsAndFormFieldsValidity$();
  readonly #checkBuyerQuestionnaireFormAndFormFieldsValidity$ =
    this.checkBuyerQuestionnaireFormAndFormFieldsValidity$();
  readonly #checkSelectedTicketAssignmentFormsAndFormFieldsValidity$ =
    this.checkSelectedTicketAssignmentFormsAndFormFieldsValidity$();

  readonly #isDuplicateEmailAddressWarningMessageModalOpen = signal(false);

  setIsDuplicateEmailAddressWarningMessageModalOpen(isModalOpen: boolean) {
    return this.#isDuplicateEmailAddressWarningMessageModalOpen.set(isModalOpen);
  }

  getIsDuplicateEmailAddressWarningMessageModalOpen() {
    return this.#isDuplicateEmailAddressWarningMessageModalOpen;
  }

  getCheckNameDuplicates$() {
    return this.#checkNameDuplicates$;
  }

  getCheckEmailAddressDuplicates$() {
    return this.#checkEmailAddressDuplicates$;
  }

  getCheckMergedTicketAssignmentFormsValidity$() {
    return this.#checkMergedTicketAssignmentFormsValidity$;
  }

  getCheckOrderOverviewFormsAndFormFieldsValidity$() {
    return this.#checkOrderOverviewFormsAndFormFieldsValidity$;
  }

  getCheckBuyerInformationFormsAndFormFieldsValidity$() {
    return this.#checkBuyerInformationFormsAndFormFieldsValidity$;
  }

  getCheckBuyerQuestionnaireFormAndFormFieldsValidity$() {
    return this.#checkBuyerQuestionnaireFormAndFormFieldsValidity$;
  }

  getCheckSelectedTicketAssignmentFormsAndFormFieldsValidity$() {
    return this.#checkSelectedTicketAssignmentFormsAndFormFieldsValidity$;
  }

  private checkBuyerInformationFormsAndFormFieldsValidity$(): Observable<CheckedFormsValidity> {
    const buyerInformationForms$ = toObservable(
      computed(
        () =>
          ({
            [FormName.BuyerInformation]: this.#formMediator.getFormState(FormName.BuyerInformation)(),
            [FormName.BuyerBillingAddress]: this.#formMediator.getFormState(FormName.BuyerBillingAddress)(),
            [FormName.BuyerPrivacyPolicy]: this.#formMediator.getFormState(FormName.BuyerPrivacyPolicy)()
          }) as CheckBuyerInformationFormsValidity
      )
    );

    const buyerInformationFormsChanges$ = buyerInformationForms$.pipe(
      map((forms) => ({ isCheckedTicketLimitPerEmailAddressCheck: false, forms, formErrors: {} }))
    );

    const checkBuyerInformationTicketLimitPerEmailAddress$ = this.#formTicketLimitValidity
      .checkBuyerInformationTicketLimitPerEmailAddress$(buyerInformationForms$)
      .pipe(
        map(({ forms, formErrors }) => ({
          isCheckedTicketLimitPerEmailAddressCheck: true,
          forms,
          formErrors
        })),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    return combineLatest({
      isTicketAssignmentStepVisible: this.#store.select(selectIsTicketAssignmentStepVisible),
      isTicketLimitPerEmailAccountActive: this.#store.select(selectIsTicketLimitPerEmailActive),
      isValidationProcessing: this.#formValidationProcessing.isValidationProcessing$
    }).pipe(
      switchMap(({ isTicketAssignmentStepVisible, isTicketLimitPerEmailAccountActive, isValidationProcessing }) =>
        iif(
          () => !isTicketAssignmentStepVisible && isTicketLimitPerEmailAccountActive,
          merge(buyerInformationFormsChanges$, checkBuyerInformationTicketLimitPerEmailAddress$).pipe(
            switchMap(({ isCheckedTicketLimitPerEmailAddressCheck, forms, formErrors }) =>
              iif(
                () => isCheckedTicketLimitPerEmailAddressCheck,
                of({ forms, formErrors }),
                of(forms).pipe(
                  withLatestFrom(checkBuyerInformationTicketLimitPerEmailAddress$),
                  map(([forms, { formErrors: ticketLimitPerEmailAddressFormErrors }]) => ({
                    forms,
                    formErrors: _merge({}, formErrors, ticketLimitPerEmailAddressFormErrors)
                  }))
                )
              )
            )
          ),
          buyerInformationFormsChanges$
        ).pipe(
          this.setFormAndFormFieldsValidityPipe(),
          map((formValidities) => {
            if (isTicketAssignmentStepVisible || !isValidationProcessing) {
              return formValidities;
            }

            return {
              ...formValidities,
              [FormName.BuyerInformation]: {
                ...formValidities[FormName.BuyerInformation],
                isValid: false
              }
            };
          })
        )
      )
    );
  }

  private checkBuyerQuestionnaireFormAndFormFieldsValidity$(): Observable<CheckedFormsValidity> {
    return runInInjectionContext(this.#injector, () => {
      const buyerQuestionnaireForms = computed(
        () =>
          ({
            [FormName.BuyerQuestionnaire]: this.#formMediator.getFormState(FormName.BuyerQuestionnaire)()
          }) as CheckBuyerQuestionnaireFormsValidity
      );

      return toObservable(buyerQuestionnaireForms).pipe(
        map((forms) => ({ forms, formErrors: {} })),
        this.setFormAndFormFieldsValidityPipe()
      );
    });
  }

  private checkSelectedTicketAssignmentFormsAndFormFieldsValidity$() {
    return combineLatest({
      checkMergedTicketAssignmentFormsValidity: this.#checkMergedTicketAssignmentFormsValidity$,
      selectedTariffUuid: this.#store.select(selectSelectedTariffUuid),
      isValidationProcessing: this.#formValidationProcessing.isValidationProcessing$
    }).pipe(
      filter(({ selectedTariffUuid }) => !isEmpty(selectedTariffUuid)),
      map(
        ({
          checkMergedTicketAssignmentFormsValidity: {
            ticketAssignmentForms,
            validatedTicketLimitPerEmailAddress,
            validatedDuplicateName,
            validatedDuplicateEmailAddress,
            mergedTicketAssignmentFormsValidity
          },
          selectedTariffUuid,
          isValidationProcessing
        }) => {
          const areAllTicketAssignmentStepFormsValid = every([...mergedTicketAssignmentFormsValidity.values()]);
          const areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing =
            areAllTicketAssignmentStepFormsValid && !isValidationProcessing;

          const mergedTicketAssignmentFormsValidityCopy = new Map(mergedTicketAssignmentFormsValidity);
          const areSelectedTariffTicketAssignmentFormsValid =
            mergedTicketAssignmentFormsValidityCopy.get(selectedTariffUuid);

          if (areSelectedTariffTicketAssignmentFormsValid) {
            mergedTicketAssignmentFormsValidityCopy.delete(selectedTariffUuid);
          }

          const nextInvalidTicketAssignmentFormTariffUuid =
            findKey(Object.fromEntries(mergedTicketAssignmentFormsValidityCopy), (isValid) => !isValid) || null;

          const selectedTicketHolderFormState = ticketAssignmentForms[FormName.TicketHolder].get(selectedTariffUuid);

          const ticketHolderAssignedErrors = isNil(selectedTicketHolderFormState?.person) && {
            ticketHolderNotAssigned: true
          };

          const currentTariffValidatedTicketLimitPerEmailAddressErrors =
            validatedTicketLimitPerEmailAddress[selectedTariffUuid]?.errors;
          const currentTariffValidatedDuplicateNameErrors = validatedDuplicateName[selectedTariffUuid]?.errors;
          const currentTariffValidatedDuplicateEmailAddressErrors =
            validatedDuplicateEmailAddress[selectedTariffUuid]?.errors;

          const validDuplicatesNameAndTicketLimitEmailAddressValidity: InvalidFormField =
            !this.isValidDuplicatesAndTicketLimitEmailAddress(
              currentTariffValidatedTicketLimitPerEmailAddressErrors,
              currentTariffValidatedDuplicateNameErrors,
              currentTariffValidatedDuplicateEmailAddressErrors
            )
              ? _merge(
                  {},
                  currentTariffValidatedTicketLimitPerEmailAddressErrors,
                  currentTariffValidatedDuplicateNameErrors,
                  currentTariffValidatedDuplicateEmailAddressErrors
                )
              : {};

          const formErrors = _merge(
            {},
            validDuplicatesNameAndTicketLimitEmailAddressValidity,
            ticketHolderAssignedErrors
          );

          const questionnaireForms = ticketAssignmentForms[FormName.TicketHolderQuestionnaire];
          const selectedTicketHolderQuestionnaireFormState =
            (!isEmpty(questionnaireForms) && questionnaireForms.get(selectedTariffUuid)) || null;

          return {
            selectedTariffUuid,
            areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing,
            nextInvalidTicketAssignmentFormTariffUuid,
            formErrors,
            ticketAssignmentForms: {
              [FormName.TicketHolder]: selectedTicketHolderFormState,
              [FormName.TicketHolderQuestionnaire]: selectedTicketHolderQuestionnaireFormState
            }
          };
        }
      ),
      switchMap(
        ({
          areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing,
          nextInvalidTicketAssignmentFormTariffUuid,
          formErrors,
          ticketAssignmentForms
        }) =>
          iif(
            () => areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing,
            of<CheckedSelectedTicketAssignmentFormsAndFormFieldsValidity>({
              areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing,
              nextInvalidTicketAssignmentFormTariffUuid,
              checkedFormsValidity: {
                [FormName.TicketHolder]: {
                  invalidFormFields: {},
                  formErrors: {},
                  isValid: true
                },
                [FormName.TicketHolderQuestionnaire]: {
                  invalidFormFields: {},
                  formErrors: {},
                  isValid: true
                }
              }
            }),
            of({ forms: ticketAssignmentForms, formErrors }).pipe(
              this.setFormAndFormFieldsValidityPipe(),
              map((checkedFormsValidity): CheckedSelectedTicketAssignmentFormsAndFormFieldsValidity => ({
                areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing,
                nextInvalidTicketAssignmentFormTariffUuid,
                checkedFormsValidity
              }))
            )
          )
      )
    );
  }

  private checkOrderOverviewFormsAndFormFieldsValidity$() {
    return combineLatest({
      checkMergedTicketAssignmentFormsValidity: this.#checkMergedTicketAssignmentFormsValidity$,
      isValidationProcessing: this.#formValidationProcessing.isValidationProcessing$
    }).pipe(
      map(
        ({
          checkMergedTicketAssignmentFormsValidity: {
            ticketAssignmentForms,
            validatedTicketLimitPerEmailAddress,
            validatedDuplicateName,
            validatedDuplicateEmailAddress,
            mergedTicketAssignmentFormsValidity
          },
          isValidationProcessing
        }) => {
          const orderOverviewFormsValidity = new Map();

          const ticketHolderForms = ticketAssignmentForms[FormName.TicketHolder];
          const ticketHolderFormTariffUuids = [...ticketHolderForms.keys()];
          const validFormTariffUuids = keys(
            pickBy(Object.fromEntries(mergedTicketAssignmentFormsValidity), (isValid) => isValid)
          );

          forEach(ticketHolderFormTariffUuids, (tariffUuid) => {
            const ticketHolderFormState = ticketHolderForms.get(tariffUuid) as TicketHolderFormState;
            const ticketHolderFormGroup = ticketHolderFormState.form.formGroup;
            const ticketHolderPerson = ticketHolderFormState.person;
            const ticketHolderDynamicFormFields = ticketHolderFormState.dynamicFormFields;

            const salutationFormControl = ticketHolderFormGroup?.get(FieldKey.Gender);
            const firstNameFormControl = ticketHolderFormGroup?.get(FieldKey.FirstName);
            const lastNameFormControl = ticketHolderFormGroup?.get(FieldKey.LastName);
            const emailFormControl = ticketHolderFormGroup.get(FieldKey.Email);

            const salutation = defaultTo(salutationFormControl?.value, '');
            const firstName = defaultTo(firstNameFormControl?.value, '');
            const lastName = defaultTo(lastNameFormControl?.value, '');
            const email = defaultTo(emailFormControl?.value, '');
            const personType = ticketHolderPerson?.type;

            const currentTariffValidatedTicketLimitPerEmailAddressErrors =
              validatedTicketLimitPerEmailAddress[tariffUuid]?.errors;
            const currentTariffValidatedDuplicateNameErrors = validatedDuplicateName[tariffUuid]?.errors;
            const currentTariffValidatedDuplicateEmailAddressErrors =
              validatedDuplicateEmailAddress[tariffUuid]?.errors;

            const validDuplicatesNameAndTicketLimitEmailAddressValidity: InvalidFormField =
              !this.isValidDuplicatesAndTicketLimitEmailAddress(
                currentTariffValidatedTicketLimitPerEmailAddressErrors,
                currentTariffValidatedDuplicateNameErrors,
                currentTariffValidatedDuplicateEmailAddressErrors
              )
                ? _merge(
                    {},
                    currentTariffValidatedTicketLimitPerEmailAddressErrors,
                    currentTariffValidatedDuplicateNameErrors,
                    currentTariffValidatedDuplicateEmailAddressErrors
                  )
                : {};

            const isValidFormTariff = some(validFormTariffUuids, (validTariffUuid) => validTariffUuid === tariffUuid);

            if (isValidFormTariff) {
              const formValidity: FormValidity = {
                formErrors: { ...validDuplicatesNameAndTicketLimitEmailAddressValidity },
                invalidFormFields: null,
                isValid: !isValidationProcessing
              };

              orderOverviewFormsValidity.set(tariffUuid, {
                ticketHolder: {
                  salutation,
                  firstName,
                  lastName,
                  email,
                  personType
                },
                formValidity
              });

              return;
            }

            const ticketHolderAssignedErrors = isNil(ticketHolderFormState.person) && {
              ticketHolderNotAssigned: true
            };

            const formErrors = _merge(
              {},
              validDuplicatesNameAndTicketLimitEmailAddressValidity,
              ticketHolderAssignedErrors
            );

            const ticketHolderInvalidFormFields = this.setInvalidFormFields(
              ticketHolderFormGroup.controls,
              ticketHolderDynamicFormFields
            );
            const invalidFormFields = !isEmpty(ticketHolderInvalidFormFields) ? ticketHolderInvalidFormFields : null;

            const formValidity: FormValidity = {
              formErrors,
              invalidFormFields,
              isValid: false
            };

            orderOverviewFormsValidity.set(tariffUuid, {
              ticketHolder: {
                salutation,
                firstName,
                lastName,
                email,
                personType
              },
              formValidity
            });
          });

          return orderOverviewFormsValidity;
        }
      ),
      shareReplay(1)
    );
  }

  private checkNameDuplicates$(): Observable<CheckedTariffNameValidity> {
    return combineLatest({
      uniqueVisitorCheckType: this.#store.select(uniqueVisitorCheckType),
      groupedNameEmailAddressAndPersonsByTariffUuids: this.#groupedNameEmailAddressAndPersonsByTariffUuids$
    }).pipe(
      filter(
        ({ uniqueVisitorCheckType, groupedNameEmailAddressAndPersonsByTariffUuids }) =>
          includes(
            [UniqueVisitorCheckType.WarnIfDuplicate, UniqueVisitorCheckType.MustBeUnique],
            uniqueVisitorCheckType
          ) && !isEmpty(groupedNameEmailAddressAndPersonsByTariffUuids)
      ),
      map(({ uniqueVisitorCheckType, groupedNameEmailAddressAndPersonsByTariffUuids }) => ({
        uniqueVisitorCheckType,
        groupedNamesByTariffUuids: mapValues(
          pickBy(groupedNameEmailAddressAndPersonsByTariffUuids, ({ name }) => !isEmpty(name)),
          ({ name }) => name
        )
      })),
      map(({ uniqueVisitorCheckType, groupedNamesByTariffUuids }) =>
        mapValues(
          fromPairs(
            flatMap(
              pickBy(invertBy(groupedNamesByTariffUuids), (uuids) => uuids.length > 1),
              (uuids, name) => _map(uuids, (uuid) => [uuid, name])
            )
          ),
          (name) => ({
            label: '',
            value: name,
            errors: {
              [FormError.DuplicateName]: uniqueVisitorCheckType
            }
          })
        )
      ),
      debounceTime(150),
      share(),
      startWith({})
    );
  }

  private checkEmailAddressDuplicates$(): Observable<CheckedTariffEmailAddressValidity> {
    return combineLatest({
      uniqueVisitorCheckType: this.#store.select(uniqueVisitorCheckType),
      groupedNameEmailAddressAndPersonsByTariffUuids: this.#groupedNameEmailAddressAndPersonsByTariffUuids$
    }).pipe(
      filter(
        ({ uniqueVisitorCheckType, groupedNameEmailAddressAndPersonsByTariffUuids }) =>
          includes(
            [UniqueVisitorCheckType.WarnIfDuplicate, UniqueVisitorCheckType.MustBeUnique],
            uniqueVisitorCheckType
          ) && !isEmpty(groupedNameEmailAddressAndPersonsByTariffUuids)
      ),
      map(({ uniqueVisitorCheckType, groupedNameEmailAddressAndPersonsByTariffUuids }) => ({
        uniqueVisitorCheckType,
        groupedEmailAddressByTariffUuids: mapValues(
          pickBy(groupedNameEmailAddressAndPersonsByTariffUuids, ({ email }) => !isEmpty(email)),
          ({ email }) => email
        )
      })),
      map(({ uniqueVisitorCheckType, groupedEmailAddressByTariffUuids }) =>
        mapValues(
          fromPairs(
            flatMap(
              pickBy(invertBy(groupedEmailAddressByTariffUuids), (uuids) => uuids.length > 1),
              (uuids, email) => _map(uuids, (uuid) => [uuid, email])
            )
          ),
          (email) => ({
            label: '',
            value: email,
            errors: {
              [FormError.DuplicateEmailAddress]: uniqueVisitorCheckType
            }
          })
        )
      ),
      debounceTime(150),
      share(),
      startWith({})
    );
  }

  private checkMergedTicketAssignmentFormsValidity$() {
    return combineLatest({
      checkTicketAssignmentTicketLimitPerEmailAddress:
        this.#formTicketLimitValidity.getCheckTicketAssignmentTicketLimitPerEmailAddress$(),
      validatedDuplicateName: this.#checkNameDuplicates$,
      validatedDuplicateEmailAddress: this.#checkEmailAddressDuplicates$
    }).pipe(
      filter(
        ({ checkTicketAssignmentTicketLimitPerEmailAddress: { forms: ticketAssignmentForms } }) =>
          !isEmpty(ticketAssignmentForms[FormName.TicketHolder])
      ),
      map(
        ({
          checkTicketAssignmentTicketLimitPerEmailAddress: {
            forms: ticketAssignmentForms,
            formErrors: validatedTicketLimitPerEmailAddress
          },
          validatedDuplicateName,
          validatedDuplicateEmailAddress
        }) => {
          const mergedTicketAssignmentFormsValidity = new Map<TariffUuid, boolean>();

          const ticketHolderForms = ticketAssignmentForms[FormName.TicketHolder] as TicketHolderFormStates;
          const ticketHolderQuestionnaireForms = ticketAssignmentForms[
            FormName.TicketHolderQuestionnaire
          ] as TicketHolderQuestionnaireFormStates;

          const noTicketAssignmentQuestionnaireFormsAreActive = isEmpty(ticketHolderQuestionnaireForms);

          const ticketHolderTariffUuids = [...ticketHolderForms.keys()];

          forEach(ticketHolderTariffUuids, (tariffUuid) => {
            const {
              form: { formGroup },
              person
            } = ticketHolderForms.get(tariffUuid);
            const { valid, disabled } = formGroup;

            const isFormValid = valid || disabled;
            const isTicketHolderAssigned = !isNil(person);
            const isValidDuplicatesNameEmailAddressAndTicketLimitEmailAddress =
              this.isValidDuplicatesAndTicketLimitEmailAddress(
                validatedTicketLimitPerEmailAddress[tariffUuid]?.errors,
                validatedDuplicateName[tariffUuid]?.errors,
                validatedDuplicateEmailAddress[tariffUuid]?.errors
              );

            const isTicketHolderFormValid =
              isFormValid && isTicketHolderAssigned && isValidDuplicatesNameEmailAddressAndTicketLimitEmailAddress;

            const isTicketHolderQuestionnaireEmptyOrIsFormValid = this.setIsTicketHolderQuestionnaireEmptyOrIsFormValid(
              ticketHolderQuestionnaireForms,
              noTicketAssignmentQuestionnaireFormsAreActive,
              tariffUuid
            );

            mergedTicketAssignmentFormsValidity.set(
              tariffUuid,
              isTicketHolderFormValid && isTicketHolderQuestionnaireEmptyOrIsFormValid
            );
          });

          return {
            ticketAssignmentForms,
            validatedTicketLimitPerEmailAddress,
            validatedDuplicateName,
            validatedDuplicateEmailAddress,
            mergedTicketAssignmentFormsValidity
          };
        }
      ),
      shareReplay(1)
    );
  }

  private setFormAndFormFieldsValidityPipe() {
    return pipe<
      Observable<{ forms: SetCreateFormGroupsFormMediatorForms; formErrors: ValidationErrors }>,
      Observable<CheckedFormsValidity>,
      Observable<CheckedFormsValidity>
    >(
      switchMap(({ forms, formErrors }) =>
        combineLatest(this.setCreatedFormState(forms)).pipe(
          map((changedForms) =>
            _reduce(
              changedForms,
              (formsValidity, { dynamicFormFields, formGroup, person }, formName) => {
                const formValidity: FormValidity = {
                  invalidFormFields: {},
                  formErrors,
                  isValid: isEmpty(formErrors)
                };

                if ((isNull(dynamicFormFields) && isNull(formGroup)) || isNil(person)) {
                  formsValidity[formName] = formValidity;

                  return formsValidity;
                }

                const { controls, valid, disabled } = formGroup;

                formValidity.isValid = (valid || disabled) && isEmpty(formErrors);

                formValidity.invalidFormFields = this.setInvalidFormFields(controls, dynamicFormFields);

                formsValidity[formName] = formValidity;

                return formsValidity;
              },
              {} as CheckedFormsValidity
            )
          )
        )
      ),
      debounceTime(150)
    );
  }

  private setCreatedFormState(formMediatorForms: SetCreateFormGroupsFormMediatorForms) {
    return _reduce(
      formMediatorForms,
      (createdFormMediatorForms, formState, formName) => {
        const dynamicFormFields = isNil(formState?.form?.formGroup) ? null : formState.dynamicFormFields;
        const formGroup = isNil(formState?.form?.formGroup) ? null : formState.form.formGroup;
        const person = isNil(formState?.person) ? null : formState?.person;

        createdFormMediatorForms[formName] = of({ dynamicFormFields, formGroup, person });

        return createdFormMediatorForms;
      },
      {} as CreatedFormGroups
    );
  }

  private setInvalidFormFields(controls: Record<string, AbstractControl>, dynamicFormFields: DynamicFormFields) {
    return _reduce(
      controls,
      (invalidFields, formControl, fieldKey) => {
        const dynamicFormField = find(dynamicFormFields, { key: fieldKey })!;
        const { value, errors } = formControl;

        if (isNil(errors) || dynamicFormField.isHidden) {
          return invalidFields;
        }

        const label = removeHtmlMarkup(this.setInvalidFormFieldLabel(dynamicFormField, formControl));

        invalidFields[fieldKey] = {
          label,
          value,
          errors
        };

        return invalidFields;
      },
      {}
    );
  }

  private setInvalidFormFieldLabel(dynamicFormField: DynamicFormField, formControl: AbstractControl) {
    const { label } = dynamicFormField;

    if (isCheckboxField(dynamicFormField)) {
      const isQuestionnaireCheckbox = !isEmpty(label);

      if (isQuestionnaireCheckbox) {
        return label;
      }

      const { options } = dynamicFormField;
      const invalidCheckboxOption = find(options, ({ key }) => !!formControl.get(`${key}`)?.invalid);

      return defaultTo(invalidCheckboxOption, head(options)!).label;
    }

    return label;
  }

  private setIsTicketHolderQuestionnaireEmptyOrIsFormValid(
    ticketHolderQuestionnaireForms: TicketHolderQuestionnaireFormStates,
    noTicketAssignmentQuestionnaireFormsAreActive: boolean,
    tariffUuid: TariffUuid
  ) {
    const noTicketHolderQuestionnaireFormIsActive =
      noTicketAssignmentQuestionnaireFormsAreActive && !has(ticketHolderQuestionnaireForms, 'get');

    if (noTicketHolderQuestionnaireFormIsActive) {
      return true;
    }

    const questionnaireFormState = ticketHolderQuestionnaireForms?.get(tariffUuid);

    if (isNil(questionnaireFormState)) {
      return true;
    }

    const { valid, disabled } = questionnaireFormState.form.formGroup;

    return valid || disabled;
  }

  private isValidDuplicatesAndTicketLimitEmailAddress(
    validatedTicketLimitPerEmailAddressErrors: ValidationErrors,
    validatedDuplicateNamesErrors: ValidationErrors,
    validatedDuplicateEmailAddressErrors: ValidationErrors
  ) {
    if (
      isNil(validatedTicketLimitPerEmailAddressErrors) &&
      isNil(validatedDuplicateNamesErrors) &&
      isNil(validatedDuplicateEmailAddressErrors)
    ) {
      return true;
    }

    const hasValidatedTicketLimitPerEmailAddressError = has(
      validatedTicketLimitPerEmailAddressErrors,
      FormError.TicketLimitPerEmailAddress
    );

    return (
      !hasValidatedTicketLimitPerEmailAddressError &&
      this.isDuplicateNameFieldValid(validatedDuplicateNamesErrors) &&
      this.isDuplicateEmailAddressFieldValid(validatedDuplicateEmailAddressErrors)
    );
  }

  private isDuplicateNameFieldValid(validationErrors: ValidationErrors) {
    return validationErrors?.duplicateName !== UniqueVisitorCheckType.MustBeUnique;
  }

  private isDuplicateEmailAddressFieldValid(validationErrors: ValidationErrors) {
    return validationErrors?.duplicateEmailAddress !== UniqueVisitorCheckType.MustBeUnique;
  }
}
